import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const RECAPTCHA_SITE_KEY = '6LeXHp4sAAAAADBX2baUxrISBNKcUUJeC3OSYC4d';

const AdminLoginPage = () => {
  const { t } = useLanguage();
  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const executeRecaptcha = async (action) => {
    return new Promise((resolve) => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
            .then(resolve)
            .catch(() => resolve(null));
        });
      } else {
        resolve(null);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        // Get reCAPTCHA token for registration
        const recaptchaToken = await executeRecaptcha('register');
        await register(formData.email, formData.password, recaptchaToken);
        toast.success('Account created successfully');
      } else {
        await login(formData.email, formData.password);
        toast.success('Login successful');
      }
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${t('admin.login')} — Villa.ooo`}</title>
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-bone px-6" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-10">
            <Link to="/" className="inline-block">
              <img 
                src="/logo-villa.png" 
                alt="Villa.ooo" 
                className="h-24 mx-auto object-contain"
              />
              <span className="block text-[10px] tracking-[0.25em] uppercase text-center -mt-6" style={{color: '#1e3a5f'}}>
                Luxury Seafront
              </span>
            </Link>
            <p className="text-text-secondary mt-4">{t('admin.login')}</p>
          </div>

          <div className="bg-white p-8 border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
              <div>
                <label className="label-style block mb-3">{t('admin.email')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-minimal w-full"
                  required
                  data-testid="admin-email-input"
                />
              </div>

              <div>
                <label className="label-style block mb-3">{t('admin.password')}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-minimal w-full"
                  required
                  minLength={6}
                  data-testid="admin-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-4 text-sm tracking-widest uppercase"
                data-testid="admin-submit-btn"
              >
                {loading ? '...' : isRegister ? 'Register' : t('admin.loginBtn')}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
              <button
                type="button"
                onClick={() => setIsRegister(!isRegister)}
                className="text-text-secondary hover:text-gold transition-colors text-sm"
                data-testid="toggle-register-btn"
              >
                {isRegister 
                  ? 'Already have an account? Login' 
                  : "Don't have an account? Register"
                }
              </button>
              
              {!isRegister && (
                <div>
                  <Link
                    to="/forgot-password"
                    className="text-text-secondary hover:text-gold transition-colors text-sm"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default AdminLoginPage;
